import React, { useState, useEffect } from 'react';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import Menu from '../menu/Menu';
import { useLocation } from 'react-router-dom';
import apiClient from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import '../complaints/ComplaintManagement.css';

const AddPaymentType = ({ type, onClose, embedded = false }) => {
  const location = useLocation();
  const isEditMode = type && type.id;
  
  const [form, setForm] = useState({
    name: '',
    status: 'ACTIVE'
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEditMode) {
      setForm({
        name: type.name || '',
        status: type.status || 'ACTIVE'
      });
    } else {
      setForm({
        name: '',
        status: 'ACTIVE'
      });
    }
  }, [type, isEditMode]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.name.trim()) {
      newErrors.name = 'Payment type name is required';
    }
    if (!form.status) {
      newErrors.status = 'Status is required';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        status: form.status
      };

      if (isEditMode) {
        await apiClient.post(`/payments/types/${type.id}/update`, payload);
        toastSuccess('Payment type updated successfully!');
      } else {
        const response = await apiClient.post('/payments/types', payload);
        
        // Handle the new response structure that indicates reactivation
        const result = response.data;
        
        if (result.was_reactivated) {
          toastSuccess(result.message || 'Payment type was reactivated successfully!');
        } else {
          toastSuccess(result.message || 'Payment type created successfully!');
        }
      }

      onClose();
    } catch (error) {
      console.error('Error saving payment type:', error);
      
      // Show comprehensive error messages
      let errorMessage = 'Failed to save payment type';
      
      // Check for duplicate errors more comprehensively
      const errorDetail = error.response?.data?.detail || '';
      const errorMessage_response = error.response?.data?.message || '';
      const allErrorText = (errorDetail + ' ' + errorMessage_response).toLowerCase();
      
      if (error.response?.status === 400 && allErrorText.includes('already exists')) {
        errorMessage = 'A payment type with this name already exists';
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid data provided';
      } else if (error.response?.status === 404) {
        errorMessage = 'Payment type not found';
      }
      
      toastError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  if (embedded) {
    return (
      <div>
        <div style={{ background: 'transparent' }}>
          <h2 className="rt-title">{isEditMode ? 'Edit Payment Type' : 'Add Payment Type'}</h2>
        </div>
        
        <div className="rt-main-section">
          <form onSubmit={handleSubmit} className="complaint-form">
            <div className="complaint-form-sections">
              <div className="complaint-section">
                <h3 className="complaint-section-title">Payment Type Details</h3>
                
                <div className="complaint-form-group">
                  <label>
                    Payment Type Name <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Enter payment type name (e.g., Cash, Bank Transfer, UPI, Card)"
                    required
                    disabled={loading}
                  />
                  {errors.name && (
                    <span className="form-error">{errors.name}</span>
                  )}
                </div>

                <div className="complaint-form-group">
                  <label>
                    Status <span style={{ color: 'red' }}>*</span>
                  </label>
                  <select
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                  {errors.status && (
                    <span className="form-error">{errors.status}</span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Loading Overlay */}
            {loading && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 9999
              }}>
                <RTSpinner size="medium" showMessage={true} message="Saving payment type..." />
              </div>
            )}
            
            <div className="complaint-actions">
              <RTButton 
                type="button" 
                variant="secondary" 
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </RTButton>
              <RTButton 
                type="submit" 
                variant="primary" 
                disabled={loading}
              >
                {loading ? 'Saving...' : (isEditMode ? 'Update Payment Type' : 'Add Payment Type')}
              </RTButton>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="rt-layout">
      <Menu className="rt-menu" activePath={location.pathname} />
      <div className="rt-main-content">
        <div className="rt-title-row">
          <h2 className="rt-title">{isEditMode ? 'Edit Payment Type' : 'Add Payment Type'}</h2>
          <div className="rt-actions">
            <RTButton variant="secondary" onClick={onClose}>
              ← Back
            </RTButton>
          </div>
        </div>
        
        <div className="rt-main-section">
          <form onSubmit={handleSubmit} className="complaint-form">
            <div className="complaint-form-sections">
              <div className="complaint-section">
                <h3 className="complaint-section-title">Payment Type Details</h3>
                
                <div className="complaint-form-group">
                  <label>
                    Payment Type Name <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Enter payment type name (e.g., Cash, Bank Transfer, UPI, Card)"
                    required
                    disabled={loading}
                  />
                  {errors.name && (
                    <span className="form-error">{errors.name}</span>
                  )}
                </div>

                <div className="complaint-form-group">
                  <label>
                    Status <span style={{ color: 'red' }}>*</span>
                  </label>
                  <select
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                  {errors.status && (
                    <span className="form-error">{errors.status}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Loading Overlay */}
            {loading && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 9999
              }}>
                <RTSpinner size="medium" showMessage={true} message="Saving payment type..." />
              </div>
            )}

            <div className="complaint-actions">
              <RTButton 
                type="button" 
                variant="secondary" 
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </RTButton>
              <RTButton 
                type="submit" 
                variant="primary" 
                disabled={loading}
              >
                {loading ? 'Saving...' : (isEditMode ? 'Update Payment Type' : 'Add Payment Type')}
              </RTButton>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddPaymentType;
